import { WorkbenchLink } from '@affine/core/modules/workbench';
import clsx from 'clsx';
import React, { type SVGAttributes } from 'react';
import type { To } from 'react-router-dom';

import * as styles from './index.css';

export interface CardMenuItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactElement<SVGAttributes<SVGElement>>;
  label: string;
  description?: string;
  active?: boolean;
  disabled?: boolean;
  postfix?: React.ReactElement;
  postfixDisplay?: 'always' | 'hover';
}

export interface CardMenuLinkItemProps extends CardMenuItemProps {
  to: To;
  linkComponent?: React.ComponentType<{ to: To; className?: string }>;
}

/**
 * CardMenuItem - Card-style menu item for app sidebar
 * Similar to MenuItem but with enhanced visual design (borders, shadows, descriptions)
 */
export const CardMenuItem = React.forwardRef<HTMLDivElement, CardMenuItemProps>(
  (
    {
      onClick,
      icon,
      label,
      description,
      active,
      disabled,
      postfix,
      postfixDisplay = 'hover',
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        {...props}
        onClick={onClick}
        className={clsx([styles.cardRoot, props.className])}
        data-active={active}
        data-disabled={disabled}
        tabIndex={0}
      >
        {icon && (
          <div className={styles.iconContainer}>
            {React.cloneElement(icon, {
              className: clsx([styles.icon, icon.props.className]),
            })}
          </div>
        )}

        <div className={styles.contentContainer}>
          <div className={styles.label}>{label}</div>
          {description && (
            <div className={styles.description}>{description}</div>
          )}
        </div>

        {postfix && (
          <div className={styles.postfix} data-postfix-display={postfixDisplay}>
            {postfix}
          </div>
        )}
      </div>
    );
  }
);

CardMenuItem.displayName = 'CardMenuItem';

/**
 * CardMenuLinkItem - CardMenuItem with navigation support
 */
export const CardMenuLinkItem = React.forwardRef<
  HTMLDivElement,
  CardMenuLinkItemProps
>(({ to, linkComponent: LinkComponent = WorkbenchLink, ...props }, ref) => {
  return (
    <LinkComponent to={to} className={styles.linkItemRoot}>
      <CardMenuItem ref={ref} {...props} />
    </LinkComponent>
  );
});

CardMenuLinkItem.displayName = 'CardMenuLinkItem';
